import type { AppDispatch } from '../../store';
import { setRouteEndpointPlacement, resetRouteEndpoints } from '../ui/uiSlice';

export type RoutePoint = { x: number; y: number };
export type CapturedEndpoints = { start: RoutePoint; end: RoutePoint };

/**
 * Promise bridge between a generation flow (chat widget / toolbox button) and the canvas clicks that
 * place the route start/end points. `beginEndpointCapture` switches the canvas into placement mode
 * and resolves once the canvas reports both points via `completeEndpointCapture`. Only one capture
 * runs at a time, so a module-level resolver is sufficient.
 */
let resolver: ((v: CapturedEndpoints) => void) | null = null;
let rejecter: ((e: Error) => void) | null = null;

export function beginEndpointCapture(dispatch: AppDispatch): Promise<CapturedEndpoints> {
  cancelEndpointCapture(); // clear any prior in-flight capture
  dispatch(resetRouteEndpoints());
  dispatch(setRouteEndpointPlacement('start'));
  return new Promise<CapturedEndpoints>((resolve, reject) => {
    resolver = resolve;
    rejecter = reject;
  });
}

/** Canvas calls this once the END point has been placed. */
export function completeEndpointCapture(points: CapturedEndpoints): void {
  const r = resolver;
  resolver = null;
  rejecter = null;
  r?.(points);
}

/** Cancel an in-flight capture (Esc, or a new capture starting). */
export function cancelEndpointCapture(): void {
  const rej = rejecter;
  resolver = null;
  rejecter = null;
  rej?.(new Error('cancelled'));
}

export function isCapturingEndpoints(): boolean {
  return resolver !== null;
}
